import { styled } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useLogo } from 'hooks/useLogo'

// Inside an iframe 100vh is the iframe's own height; a host that sizes the
// iframe to its content would then never let it shrink.
const Wrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'embedded'
})<{ embedded: boolean }>(({ theme, embedded }) => ({
  background: theme.palette.primarySoft.main,
  padding: theme.spacing(3),
  minHeight: embedded ? undefined : '100vh'
}))

const Container = styled('div', {
  shouldForwardProp: prop => prop !== 'embedded'
})<{ embedded: boolean }>(({ theme, embedded }) => ({
  maxWidth: embedded ? '1200px' : '900px',
  width: embedded ? '80%' : undefined,
  margin: '0 auto',
  background: theme.palette.background.default,
  padding: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('md')]: {
    width: '100%'
  }
}))

const Header = styled('header')({
  marginBottom: '2rem'
})

const Section = styled('section')(({ theme }) => ({
  marginTop: '1.5rem',
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    marginTop: '2rem',
    marginBottom: '1rem',
    color: theme.palette.primary.main
  },
  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginTop: '2rem',
    marginBottom: '1rem',
    paddingBottom: '0.5rem',
    borderBottom: `2px solid ${theme.palette.divider}`
  },
  '& h3': {
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
    fontSize: '1.25rem',
    fontWeight: 600
  },
  '& h4': {
    marginTop: '1rem',
    marginBottom: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  '& details': {
    marginBottom: '1rem',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    padding: '0.5rem',
    '&[open]': {
      paddingBottom: '1rem'
    },
    '& summary': {
      fontSize: '1.2rem',
      fontWeight: 600,
      cursor: 'pointer',
      padding: '0.5rem',
      listStyle: 'none',
      '&::-webkit-details-marker': {
        display: 'none'
      },
      '&::marker': {
        display: 'none'
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderRadius: '4px'
      }
    }
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    marginBlock: '1rem',
    display: 'block'
  },
  '& ul, & ol': {
    marginLeft: '1.5rem',
    lineHeight: 1.6,
    paddingLeft: '1rem'
  },
  '& p': {
    marginBottom: '0.5rem',
    lineHeight: 1.6
  },
  '& strong': {
    fontWeight: 600
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginBlock: '1rem',
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: '0.75rem',
      textAlign: 'left'
    },
    '& th': {
      backgroundColor: theme.palette.action.hover,
      fontWeight: 600
    },
    '& tr:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: '2rem 0'
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginLeft: 0,
    paddingLeft: '1rem',
    color: theme.palette.text.secondary
  },
  '& code': {
    backgroundColor: theme.palette.action.hover,
    padding: '0.2rem 0.4rem',
    borderRadius: '4px',
    fontSize: '0.9em',
    fontFamily: 'monospace'
  },
  '& pre': {
    backgroundColor: theme.palette.action.hover,
    padding: '1rem',
    borderRadius: '8px',
    overflow: 'auto',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0
    }
  },
  [theme.breakpoints.down('sm')]: {
    '& h1': {
      fontSize: '1.6rem'
    },
    '& h2': {
      fontSize: '1.3rem'
    },
    '& h3': {
      fontSize: '1.1rem'
    },
    '& img': {
      marginInline: '-0.5rem',
      maxWidth: 'calc(100% + 1rem)',
      borderRadius: '4px'
    },
    '& details summary': {
      fontSize: '1rem'
    },
    '& table': {
      fontSize: '0.9rem',
      '& th, & td': {
        padding: '0.5rem'
      }
    }
  }
}))

const LoadingMessage = styled('p')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  padding: '2rem'
}))

const ErrorMessage = styled('p')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.error.main,
  padding: '2rem',
  backgroundColor: theme.palette.error.light,
  borderRadius: '8px'
}))

/**
 * XRayManual component - Public page for X-Ray AI Analysis Manual
 * Renders markdown content from public/manual-xray-content.md
 * Accessible without authentication at /manual/xray
 */
export default function XRayManual() {
  const logo = useLogo()
  const embedded = window.self !== window.top
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // SEO: Update document title and meta tags
  useEffect(() => {
    const originalTitle = document.title
    document.title = 'Manual de Análise de Raio-X por IA | sentinela'

    // Update or create meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    const originalDescription = metaDescription?.getAttribute('content') || ''

    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Guia completo para profissionais de saúde sobre como usar o sistema de análise de Raio-X por Inteligência Artificial da plataforma sentinela. Aprenda a acessar, filtrar, analisar e gerar relatórios.'
      )
    } else {
      const newMeta = document.createElement('meta')
      newMeta.setAttribute('name', 'description')
      newMeta.setAttribute(
        'content',
        'Guia completo para profissionais de saúde sobre como usar o sistema de análise de Raio-X por Inteligência Artificial da plataforma sentinela.'
      )
      document.head.appendChild(newMeta)
    }

    // Cleanup: restore original values on unmount
    return () => {
      document.title = originalTitle
      if (metaDescription && originalDescription) {
        metaDescription.setAttribute('content', originalDescription)
      }
    }
  }, [])

  // Fetch manual content
  useEffect(() => {
    fetch('/manual-xray-content.md')
      .then(res => {
        if (!res.ok) throw new Error('Falha ao carregar o manual')
        return res.text()
      })
      .then(text => {
        setContent(text)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  return (
    <Wrapper embedded={embedded}>
      <Container embedded={embedded}>
        <Header>
          <img
            src={logo}
            alt='sentinela logo'
            style={{ maxWidth: '300px', marginBottom: '1rem' }}
          />
        </Header>

        {loading && <LoadingMessage>Carregando manual...</LoadingMessage>}
        {error && <ErrorMessage>Erro ao carregar manual: {error}</ErrorMessage>}

        {content && (
          <Section>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </Section>
        )}
      </Container>
    </Wrapper>
  )
}
